import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnInit,
  output,
  OutputEmitterRef,
  viewChildren,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import {
  MatError,
  MatFormField,
  MatInput,
  MatSuffix,
} from '@angular/material/input';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { first, of } from 'rxjs';
import { NgStyle } from '@angular/common';
import { catchError } from 'rxjs/operators';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { AnimatedTooltipComponent } from '../../../components/animated-tooltip/animated-tooltip.component';
import { MfaService } from '../../../core/http/mfa.service';
import { MfaFailComponent } from '../../../components/mfa-fail/mfa-fail.component';
import { SetUpTwoFactorScreen } from '../set-up-two-factor.component';
import { MatIcon } from '@angular/material/icon';
import { MfaErrors } from '../../../core/state/mfa/mfa.state';

@Component({
  selector: 'app-set-up-app',
  imports: [
    TranslateModule,
    MatButton,
    MatInput,
    ReactiveFormsModule,
    FormsModule,
    MatFormField,
    MatError,
    MatFormField,
    MatProgressSpinner,
    AnimatedTooltipComponent,
    NgStyle,
    MfaFailComponent,
    MatSuffix,
    MatIcon,
  ],
  templateUrl: './set-up-app.component.html',
  styleUrls: [
    './set-up-app.component.scss',
    '../../login-form/login-form.component.scss',
    '../set-up-two-factor.component.scss',
  ],
})
export class SetUpAppComponent implements OnInit, AfterViewChecked {
  sections = viewChildren('section');

  dismiss: OutputEmitterRef<void> = output<void>();
  changeScreen: OutputEmitterRef<SetUpTwoFactorScreen> =
    output<SetUpTwoFactorScreen>();
  mfa: { key: string; qrCodeUrl: string } | null = null;
  otpForm: FormGroup;
  isLoading = false;
  mfaLoaded = false;
  tooltipStep: number;
  sectionHeights: number[] = [];
  isActivating = false;

  private mfaService = inject(MfaService);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.isLoading = true;
    this.mfaService
      .getMfaSetupCode()
      .pipe(first())
      .pipe(
        catchError(() => {
          this.isLoading = false;
          this.mfaLoaded = true;
          this.cdr.detectChanges();
          return of(null);
        }),
      )
      .subscribe((mfa) => {
        this.isLoading = false;
        this.mfaLoaded = true;
        if (mfa?.key && mfa.qrCodeUrl) {
          this.tooltipStep = 0;
          this.mfa = {
            key: mfa.key?.replace(/(.{4})/g, '$1 ').trim(),
            qrCodeUrl: mfa.qrCodeUrl,
          };
        }
        this.cdr.detectChanges();
      });

    this.otpForm = this.fb.group({
      mfaCode: new FormControl('', [
        Validators.required,
        Validators.pattern('[0-9]{6}'),
      ]),
    });
  }

  ngAfterViewChecked() {
    if (this.mfaLoaded) {
      setTimeout(() => {
        this.getSectionHeights();
        this.mfaLoaded = false;
      });
    }
  }

  getSectionHeights() {
    this.sectionHeights = (this.sections() || []).map(
      (section: ElementRef, index: number) => {
        if (index < 2) {
          return (
            section.nativeElement.offsetTop +
            section.nativeElement.offsetHeight / 2 -
            30
          );
        } else {
          return (
            section.nativeElement.offsetTop +
            section.nativeElement.offsetHeight / 2 -
            219
          );
        }
      },
    );
  }

  onActivate() {
    this.isActivating = true;
    const mfaCode = this.otpForm.get('mfaCode');
    this.mfaService
      .setUpMfa(mfaCode?.value)
      .pipe(first())
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.isActivating = false;
          if (error.status === 403) {
            mfaCode.setErrors({ incorrect: true });
          } else {
            this.mfa = null;
          }
          return of(null);
        }),
      )
      .subscribe((res) => {
        this.isActivating = false;
        if (res) {
          this.changeScreen.emit(SetUpTwoFactorScreen.FINAL);
        }
      });
  }

  onDismiss() {
    this.dismiss.emit();
  }

  onTooltipStepChange(step: number) {
    this.tooltipStep = step - 1;
  }

  protected readonly MfaErrors = MfaErrors;
}
